import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDownRight,
  ArrowUpRight,
  ClipboardCheck,
  Cpu,
  Gauge,
  MapPin,
  Radio,
  Search,
  Truck,
} from "lucide-react";
import "@/App.css";

const heroImage =
  "https://gorillava.com/wp-content/themes/gorilla-va-theme/assets/img/gorilla-strong-craftsman.jpg";

const tools = [
  {
    id: "stoplight",
    title: "Stoplight",
    code: "OPS-01",
    status: "LIVE // 14 GREEN / 0 RED",
    statusTone: "live",
    icon: Gauge,
    description:
      "Site go/no-go safety matrix, weather hazard alerts, compliance checks, and crew readiness clearance.",
    metrics: "14 ACTIVE SITES  /  100% OSHA CLEARED  /  0 STOPPAGES",
    actionLabel: "Launch Stoplight Radar",
    url: "https://apartment-logger.emergent.host/?utm_source=share",
    testId: "tile-stoplight",
    size: "wide",
    image:
      "https://gorillava.com/wp-content/uploads/2026/07/pm-safety-harness.jpg",
    imageAlt: "Gorilla field worker wearing a safety harness on site",
  },
  {
    id: "dispatch-hub",
    title: "Dispatch Hub",
    code: "OPS-02",
    status: "DISPATCHING // 18 UNITS",
    statusTone: "amber",
    icon: Radio,
    description:
      "Rapid crew allocation, equipment telematics, vehicle routing, and 24/7 emergency callout deployment.",
    metrics: "18 FIELD CREWS  /  38M AVG DEPLOY  /  6 IN TRANSIT",
    actionLabel: "Open Dispatch Matrix",
    url: "https://dispatch-hub-531.emergent.host/?utm_source=share",
    testId: "tile-dispatch-hub",
    size: "wide",
    image:
      "https://gorillava.com/wp-content/themes/gorilla-va-theme/assets/img/restoration-van.jpg",
    imageAlt: "Gorilla Contracting rapid response service van",
  },
  {
    id: "quote-scope-ai",
    title: "Quote Scope AI",
    code: "OPS-03",
    status: "AI ENGINE READY // V4.2",
    statusTone: "blue",
    icon: Cpu,
    description:
      "Blueprint takeoff, material price indexing, and instant commercial scope building for faster bids.",
    metrics: "99.4% TAKEOFF ACCURACY  /  <4M PROPOSAL GEN",
    actionLabel: "Run Scope Engine",
    url: "https://quote-scope-ai-1.emergent.host/?utm_source=share",
    testId: "tile-quote-scope-ai",
    size: "standard",
  },
  {
    id: "gorilla-logistics",
    title: "Gorilla Logistics",
    code: "OPS-04",
    status: "SUPPLY CHAIN STAGED",
    statusTone: "amber",
    icon: Truck,
    description:
      "Warehouse inventory, staging yard assets, trailer reservations, and scheduled jobsite drop-offs.",
    metrics: "42K SQ FT YARD  /  94% IN STOCK  /  8 DROPS TODAY",
    actionLabel: "Enter Logistics Yard",
    url: "https://gorilla-logistics.emergent.host",
    testId: "tile-gorilla-logistics",
    size: "standard",
    image:
      "https://images.unsplash.com/photo-1767112601499-1b78cfb31a62?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxoZWF2eSUyMG1hY2hpbmVyeSUyMGV4Y2F2YXRvciUyMG5pZ2h0JTIwZGFya3xlbnwwfHx8fDE3ODg0NDUzNjN8MA&ixlib=rb-4.1.0&q=85",
    imageAlt: "Industrial machinery illuminated at night",
  },
  {
    id: "renovation-log",
    title: "Renovation Log",
    code: "OPS-05",
    status: "FIELD SYNCED // 12M AGO",
    statusTone: "live",
    icon: ClipboardCheck,
    description:
      "Daily superintendent logs, milestone punch lists, inspection photos, and owner sign-offs.",
    metrics: "28 DAILY LOGS  /  4 PUNCH LISTS  /  100% SYNCED",
    actionLabel: "Access Field Logs",
    url: "https://renovation-log-1.emergent.host/?utm_source=share",
    testId: "tile-renovation-log",
    size: "standard",
    image:
      "https://gorillava.com/wp-content/themes/gorilla-va-theme/assets/img/pm-framing-wall.jpg",
    imageAlt: "Gorilla crew framing a wall on a commercial job site",
  },
];

const chapters = [
  {
    number: "01",
    label: "DISPATCH ARCHITECTURE",
    title: "Move first. Solve faster.",
    summary:
      "One command center removes vendor lag and gets the right self-performing crew moving toward the site before the problem grows.",
    bullets: ["Single point of command", "Direct crew allocation", "GPS routing and asset telemetry"],
  },
  {
    number: "02",
    label: "PRE-CONSTRUCTION INTELLIGENCE",
    title: "Make the scope precise.",
    summary:
      "From blueprint to bid, every estimate is built to move at field speed without giving up the detail commercial work demands.",
    bullets: ["Blueprint takeoff intelligence", "Regional labor indexing", "Capital improvement forecasting"],
  },
  {
    number: "03",
    label: "SITE SAFETY & VERIFICATION",
    title: "Keep every site green.",
    summary:
      "Clear gates, clean logs, and live verification turn safety from a checklist into the operating rhythm behind every project.",
    bullets: ["Weather and hazard checks", "Milestone punch verification", "Owner-ready audit trails"],
  },
];

const marqueeItems = [
  "PRE-CONSTRUCTION & PLANNING",
  "24/7 RAPID DISPATCH FIELD CREWS",
  "HAMPTON ROADS + NE NORTH CAROLINA",
  "SELF-PERFORMING COMMERCIAL TRADES",
  "ZERO COMPROMISE STRUCTURAL INTEGRITY",
  "ONE TEAM // ONE CONTRACTOR // START TO FINISH",
];

const useReveal = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!ref.current) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 },
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

const formatClock = (value) =>
  new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(value);

function BrandLockup({ footer = false }) {
  return (
    <a
      className="brand-lockup"
      href="#top"
      data-testid={footer ? "portal-footer-logo" : "portal-header-logo"}
      aria-label="GorillaVA operations home"
    >
      <img
        className="brand-logo-image"
        src="/gorilla-logo.png"
        alt="GorillaVA"
      />
    </a>
  );
}

function HeroMedia() {
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [ref, isVisible] = useReveal();

  const handlePointerMove = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = (event.clientX - bounds.left) / bounds.width - 0.5;
    const y = (event.clientY - bounds.top) / bounds.height - 0.5;
    setTilt({ x: y * -4, y: x * 5 });
  };

  return (
    <div
      ref={ref}
      className={`hero-media-wrap reveal ${isVisible ? "is-visible" : ""}`}
      style={{ "--hero-tilt-x": `${tilt.x}deg`, "--hero-tilt-y": `${tilt.y}deg` }}
      onPointerMove={handlePointerMove}
      onPointerLeave={() => setTilt({ x: 0, y: 0 })}
    >
      <div className="hero-media-frame">
        <div className="hero-media-image">
          <img src={heroImage} alt="Gorilla craftsman cutting trim on a job site" />
          <span className="hero-photo-wash" aria-hidden="true" />
        </div>
        <div className="hero-media-meta">
          <span>FIELD IMAGE / 07:42</span>
          <span>HAMPTON ROADS, VA</span>
        </div>
      </div>
      <div className="hero-media-caption">
        <span className="caption-line" aria-hidden="true" />
        <p>Built for the work that cannot wait.</p>
        <span className="caption-code">GORILLA / 24—7 RESPONSE</span>
      </div>
    </div>
  );
}

function Hero() {
  const [ref, isVisible] = useReveal();
  const heroLines = ["ONE TEAM.", "ONE COMMAND.", "FULL THROTTLE."];

  return (
    <section className="hero section-shell" ref={ref} aria-labelledby="hero-heading">
      <div className={`hero-grid reveal ${isVisible ? "is-visible" : ""}`}>
        <div className="hero-copy">
          <div className="eyebrow-row">
            <span className="eyebrow-dot" aria-hidden="true" />
            <span>INTERNAL OPERATIONS / CONTROL ROOM</span>
            <span className="eyebrow-rule" aria-hidden="true" />
            <span>VA—NC</span>
          </div>
          <h1 id="hero-heading" className="hero-title" data-testid="hero-kinetic-title">
            {heroLines.map((line, index) => (
              <span className="hero-line" key={line} style={{ "--line-index": index }}>
                <span className="hero-line-text">{line}</span>
              </span>
            ))}
          </h1>
          <p className="hero-subtitle" data-testid="hero-subtitle-manifesto">
            The internal command layer for every project, crew, quote, delivery, and field log moving through Gorilla.
          </p>
          <div className="hero-actions">
            <a className="button button-primary" href="#operations" data-testid="hero-operations-link">
              Enter operations <ArrowDownRight size={17} aria-hidden="true" />
            </a>
            <span className="hero-action-note">NO LOGIN / INTERNAL NAVIGATION ONLY</span>
          </div>
        </div>
        <HeroMedia />
      </div>
      <div className="hero-data-rail" aria-label="Portal telemetry">
        <div>
          <span className="data-label">COVERAGE</span>
          <strong>HAMPTON ROADS + NE NC</strong>
        </div>
        <div>
          <span className="data-label">OPERATING PRINCIPLE</span>
          <strong>ONE CONTRACTOR / START TO FINISH</strong>
        </div>
        <div>
          <span className="data-label">RESPONSE WINDOW</span>
          <strong>24—7 FIELD READY</strong>
        </div>
      </div>
    </section>
  );
}

function EditorialMarquee() {
  const repeatedItems = [...marqueeItems, ...marqueeItems];

  return (
    <section className="marquee-window" data-testid="editorial-marquee-ticker" aria-label="Gorilla operating principles">
      <div className="marquee-track">
        {repeatedItems.map((item, index) => (
          <span className="marquee-item" key={`${item}-${index}`}>
            {item}
            <i aria-hidden="true" />
          </span>
        ))}
      </div>
    </section>
  );
}

function OperationTile({ tool, index, onLaunch }) {
  const [ref, isVisible] = useReveal();
  const Icon = tool.icon;

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onLaunch(tool);
    }
  };

  return (
    <article
      ref={ref}
      className={`tool-tile tool-tile-${tool.size} reveal ${isVisible ? "is-visible" : ""}`}
      data-testid={tool.testId}
      style={{ "--tile-index": index }}
      role="link"
      tabIndex="0"
      onClick={() => onLaunch(tool)}
      onKeyDown={handleKeyDown}
      aria-label={`Open ${tool.title}`}
    >
      <div className="tool-tile-header">
        <span className="tool-code">{tool.code}</span>
        <span className={`tool-status tool-status-${tool.statusTone}`}>
          <span className="status-pulse" aria-hidden="true" />
          {tool.status}
        </span>
      </div>
      <div className="tool-tile-main">
        <div className="tool-icon" aria-hidden="true">
          <Icon size={26} strokeWidth={1.5} />
        </div>
        <div className="tool-copy">
          <h3>{tool.title}</h3>
          <p>{tool.description}</p>
        </div>
      </div>
      {tool.image ? (
        <div className="tool-image-frame">
          <img src={tool.image} alt={tool.imageAlt} loading="lazy" />
          <span className="tool-image-index" aria-hidden="true">
            0{index + 1}
          </span>
        </div>
      ) : (
        <div className="tool-signal-graphic" aria-hidden="true">
          <span className="signal-grid" />
          <span className="signal-line signal-line-one" />
          <span className="signal-line signal-line-two" />
          <span className="signal-value">SCOPE / READY</span>
        </div>
      )}
      <div className="tool-tile-footer">
        <span className="tool-metrics">{tool.metrics}</span>
        <button
          type="button"
          className="tool-launch"
          data-testid={`launch-${tool.id}-btn`}
          onClick={(event) => {
            event.stopPropagation();
            onLaunch(tool);
          }}
        >
          <span>{tool.actionLabel}</span>
          <ArrowUpRight size={16} aria-hidden="true" />
        </button>
      </div>
    </article>
  );
}

function Operations() {
  const [query, setQuery] = useState("");
  const [ref, isVisible] = useReveal();
  const searchRef = useRef(null);
  const filteredTools = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return tools;
    return tools.filter((tool) =>
      [tool.title, tool.description, tool.code].some((value) =>
        value.toLowerCase().includes(normalizedQuery),
      ),
    );
  }, [query]);

  useEffect(() => {
    const handleShortcut = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  const launchTool = (tool) => {
    window.open(tool.url, "_blank", "noopener,noreferrer");
  };

  return (
    <section id="operations" className="operations section-shell" ref={ref} aria-labelledby="operations-heading">
      <div className={`section-heading reveal ${isVisible ? "is-visible" : ""}`}>
        <div>
          <div className="section-kicker">
            <span>01</span>
            <span>ACTIVE TOOLCHAIN</span>
          </div>
          <h2 id="operations-heading">Everything moving.<br /><em>One view.</em></h2>
        </div>
        <p className="section-intro">
          Choose the operating layer you need. Every tool opens in a new tab so the command floor stays ready here.
        </p>
      </div>
      <div className="operations-controls">
        <label className="search-field">
          <Search size={17} aria-hidden="true" />
          <input
            ref={searchRef}
            data-testid="portal-search-input"
            aria-label="Filter operational tools"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Filter the toolchain"
          />
          <kbd>⌘ K</kbd>
        </label>
        <p className="results-count" data-testid="operations-results-count">
          {String(filteredTools.length).padStart(2, "0")} / 05 TOOLS VISIBLE
        </p>
      </div>
      <div className="operations-grid" data-testid="operations-tiles-grid">
        {filteredTools.length ? (
          filteredTools.map((tool, index) => (
            <OperationTile key={tool.id} tool={tool} index={index} onLaunch={launchTool} />
          ))
        ) : (
          <div className="no-results" data-testid="operations-no-results">
            <span>NO MATCH IN CURRENT TOOLCHAIN</span>
            <button type="button" onClick={() => setQuery("")}>Clear filter</button>
          </div>
        )}
      </div>
    </section>
  );
}

function ChapterItem({ chapter, index }) {
  const [ref, isVisible] = useReveal();

  return (
    <article
      ref={ref}
      className={`chapter reveal ${isVisible ? "is-visible" : ""}`}
      style={{ "--chapter-index": index }}
    >
      <div className="chapter-number">{chapter.number}</div>
      <div className="chapter-content">
        <span className="chapter-label">{chapter.label}</span>
        <h3>{chapter.title}</h3>
        <p>{chapter.summary}</p>
      </div>
      <ul className="chapter-bullets">
        {chapter.bullets.map((bullet) => (
          <li key={bullet}>{bullet}</li>
        ))}
      </ul>
    </article>
  );
}

function Chapters() {
  return (
    <section id="method" className="chapters section-shell" aria-labelledby="chapters-heading" data-testid="operational-chapters-section">
      <div className="chapters-heading">
        <div className="section-kicker">
          <span>02</span>
          <span>HOW WE MOVE</span>
        </div>
        <h2 id="chapters-heading">The operating<br /><em>standard.</em></h2>
        <p>Built for commercial work where response, precision, and accountability are not separate departments.</p>
      </div>
      <div className="chapter-list">
        {chapters.map((chapter, index) => (
          <ChapterItem key={chapter.number} chapter={chapter} index={index} />
        ))}
      </div>
    </section>
  );
}

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [clock, setClock] = useState(new Date());

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    const favicon = document.querySelector('link[rel="icon"]') || document.createElement("link");
    favicon.rel = "icon";
    favicon.type = "image/png";
    favicon.href = "/gorilla-logo.png";
    if (!favicon.parentNode) document.head.appendChild(favicon);
    const timer = window.setInterval(() => setClock(new Date()), 1000);
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div className="portal-app" id="top">
      <header className={`site-header ${scrolled ? "is-scrolled" : ""}`}>
        <div className="site-header-inner">
          <BrandLockup />
          <nav className="header-nav" aria-label="Portal sections">
            <a href="#operations" data-testid="portal-nav-operations">Tools <span>01</span></a>
            <a href="#method" data-testid="portal-nav-method">Standard <span>02</span></a>
          </nav>
          <div className="header-status">
            <span className="system-status" data-testid="portal-system-status-badge">
              <span className="status-pulse" aria-hidden="true" />
              SYSTEM ONLINE
            </span>
            <time dateTime={clock.toISOString()} data-testid="portal-live-clock">{formatClock(clock)} EST</time>
          </div>
        </div>
      </header>

      <main className="portal-main">
        <Hero />
        <EditorialMarquee />
        <Operations />
        <Chapters />
      </main>

      <footer className="site-footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <BrandLockup footer />
            <p>Ready for any project.<br />Built for every challenge.</p>
          </div>
          <div className="footer-location">
            <MapPin size={17} aria-hidden="true" />
            <span>Hampton Roads, VA<br />Northeast North Carolina</span>
          </div>
          <a className="footer-phone" href="tel:+17579918250" data-testid="footer-dispatch-phone">
            <span>FIELD RESPONSE / 24—7</span>
            <strong>757.991.8250</strong>
            <ArrowUpRight size={18} aria-hidden="true" />
          </a>
        </div>
        <div className="footer-base">
          <span>GORILLA CONTRACTING / INTERNAL OPERATIONS</span>
          <span>ONE TEAM / ONE CONTRACTOR / START TO FINISH</span>
        </div>
      </footer>
    </div>
  );
}

export default App;
